import { useEffect, useMemo, useRef } from 'react';
import { Film, Play } from 'lucide-react';

import { toVideoEmbed } from '@/features/teacher/components/recordings/videoEmbed';
import type { VideoSource } from '@/types/recording';

/**
 * A recorded lesson, played.
 *
 * Built once here because two panels show the same lesson — the teacher opening
 * a lesson to see who has watched it, and the child watching it — and a second
 * copy would be the one that forgot a case (CLAUDE.md §3). There are three, and
 * a screen that handles only the first is not finished:
 *
 *   uploaded    ours, in the private bucket. Played from a signed URL that
 *               expires, in a real `<video>` element so the child gets a scrub
 *               bar, volume and full screen.
 *   linked      somebody else's, and framed when the provider publishes an
 *               embed path — YouTube, Vimeo, Drive.
 *   neither     a link nobody can frame, or an upload on an install that cannot
 *               sign URLs. An honest way out, never a black rectangle.
 *
 * The frame is 16:9 at every width. No autoplay: a lesson that starts talking
 * on its own is the wrong default in a room full of children.
 */

export interface LessonPlayerProps {
  title: string;
  videoSource: VideoSource;
  /** The pasted address, on a linked lesson. */
  videoUrl: string | null;
  /** The signed, expiring address, on an uploaded one. */
  playbackUrl: string | null;
}

export function LessonPlayer({ title, videoSource, videoUrl, playbackUrl }: LessonPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoSource === 'UPLOAD' && playbackUrl && videoRef.current) {
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {});
      }
    }
  }, [videoSource, playbackUrl]);
  if (videoSource === 'UPLOAD') {
    return (
      <div className="aspect-video w-full overflow-hidden rounded-card border border-line bg-lock">
        {playbackUrl ? (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            src={playbackUrl}
            controls
            preload="metadata"
            controlsList="nodownload"
            className="h-full w-full"
          >
            {/* Reached only where the browser cannot play the container at all
                — an older Safari against a .webm, say. */}
            Your browser cannot play this recording.
          </video>
        ) : (
          <PlayerFallback
            title={title}
            message="This recording is not available to play just now. Ask the person who posted it."
          />
        )}
      </div>
    );
  }

  const embed = videoUrl ? toVideoEmbed(videoUrl) : null;
  const embedUrl = useMemo(() => {
    if (!embed?.embedUrl) return null;
    try {
      const u = new URL(embed.embedUrl);
      u.searchParams.set('autoplay', '1');
      return u.toString();
    } catch {
      return embed.embedUrl.includes('?') ? `${embed.embedUrl}&autoplay=1` : `${embed.embedUrl}?autoplay=1`;
    }
  }, [embed?.embedUrl]);

  return (
    <div className="aspect-video w-full overflow-hidden rounded-card border border-line bg-lock">
      {embed?.embedUrl ? (
        /* `allowFullScreen` and nothing else — the iframe gets no extra
           capabilities beyond what a player needs. */
        <iframe
          src={embedUrl ?? undefined}
          title={title}
          className="h-full w-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      ) : (
        <PlayerFallback
          title={title}
          message={
            embed
              ? `This lesson is hosted on ${embed.provider}, which cannot be played inside the page.`
              : 'This lesson has no video against it.'
          }
          action={
            embed && videoUrl
              ? { href: videoUrl, label: `Open it on ${embed.provider}` }
              : undefined
          }
        />
      )}
    </div>
  );
}

interface PlayerFallbackProps {
  title: string;
  message: string;
  action?: { href: string; label: string };
}

/**
 * What fills the frame when there is nothing to play in it.
 *
 * Text uses the media pair, not `text-ink`: this sits inside the player's
 * `bg-lock` frame, which is near-black in *both* themes, so the ordinary ink
 * tokens would be black on black in light mode.
 */
function PlayerFallback({ title, message, action }: PlayerFallbackProps) {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-3 p-6 text-center">
      <div className="grid h-14 w-14 place-items-center rounded-full bg-accent-soft text-accent">
        {action ? (
          <Play className="ml-0.5 h-6 w-6 fill-current" aria-hidden />
        ) : (
          <Film className="h-6 w-6" aria-hidden />
        )}
      </div>

      <h4 className="text-base text-media-ink">{title}</h4>
      <p className="max-w-md text-sm text-media-ink-2">{message}</p>

      {action && (
        <a
          href={action.href}
          target="_blank"
          rel="noreferrer"
          className="pill mt-1 bg-accent-soft text-accent transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-accent"
        >
          {action.label}
        </a>
      )}
    </div>
  );
}
